import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { api, setSessionExpiredHandler } from "./api";
import Layout from "./components/Layout.jsx";
import GlobalTopBar from "./components/GlobalTopBar.jsx";
import Login from "./pages/Login.jsx";
import PublicApply from "./pages/PublicApply.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import CourseCatalog from "./pages/CourseCatalog.jsx";
import CoursePlayer from "./pages/CoursePlayer.jsx";
import AdminOverview from "./pages/AdminOverview.jsx";
import AdminCourses from "./pages/AdminCourses.jsx";
import AdminCourseEdit from "./pages/AdminCourseEdit.jsx";
import AdminDashboard from "./pages/AdminDashboard.jsx";
import Users from "./pages/Users.jsx";
import HRHome from "./pages/HRHome.jsx";
import CareHome from "./pages/CareHome.jsx";
import QualityHome from "./pages/QualityHome.jsx";
import FinanceHome from "./pages/FinanceHome.jsx";
import GrowthHome from "./pages/GrowthHome.jsx";
import AdministrationHome from "./pages/AdministrationHome.jsx";
import PortalHome from "./pages/PortalHome.jsx";
import HRModule from "./pages/HRModule.jsx";
import Scheduling from "./pages/Scheduling.jsx";
import Accounting from "./pages/Accounting.jsx";
import Reports from "./pages/Reports.jsx";
import DataMigration from "./pages/DataMigration.jsx";
import Employees from "./pages/Employees.jsx";
import EmployeeForm from "./pages/EmployeeForm.jsx";
import EmployeeProfile from "./pages/EmployeeProfile.jsx";
import ApplicantProfile from "./pages/ApplicantProfile.jsx";
import IDCardPrint from "./pages/IDCardPrint.jsx";
import EmployeePortal from "./pages/EmployeePortal.jsx";
import PortalSchedule from "./pages/PortalSchedule.jsx";
import PortalCompliance from "./pages/PortalCompliance.jsx";
import Clients from "./pages/Clients.jsx";
import ClientForm from "./pages/ClientForm.jsx";
import ClientProfile from "./pages/ClientProfile.jsx";
import PortalCommunications from "./pages/PortalCommunications.jsx";
import FamilyPortal from "./pages/FamilyPortal.jsx";
import CommunicationSupervision from "./pages/CommunicationSupervision.jsx";
import PortalEVV from "./pages/PortalEVV.jsx";
import EVVMonitor from "./pages/EVVMonitor.jsx";
import PortalVisitCare from "./pages/PortalVisitCare.jsx";
import PortalProgressNote from "./pages/PortalProgressNote.jsx";
import AdminProgressNotes from "./pages/AdminProgressNotes.jsx";
import PortalVisitVerification from "./pages/PortalVisitVerification.jsx";
import AdminVisitVerification from "./pages/AdminVisitVerification.jsx";
import PortalMileage from "./pages/PortalMileage.jsx";
import AdminMileage from "./pages/AdminMileage.jsx";
import AdminPayroll from "./pages/AdminPayroll.jsx";
import PortalTimesheets from "./pages/PortalTimesheets.jsx";
import AdminOperations from "./pages/AdminOperations.jsx";
import AdminIntelligence from "./pages/AdminIntelligence.jsx";
import AdminBilling from "./pages/AdminBilling.jsx";
import AdminCRM from "./pages/AdminCRM.jsx";
import AdminExecutiveBI from "./pages/AdminExecutiveBI.jsx";
import FamilyMobilePortal from "./pages/FamilyMobilePortal.jsx";
import CaregiverMobilePortal from "./pages/CaregiverMobilePortal.jsx";
import AdminQualityCenter from "./pages/AdminQualityCenter.jsx";
import AdminDementiaTraining from "./pages/AdminDementiaTraining.jsx";
import AdminTraining from "./pages/AdminTraining.jsx";
import AdminCertificates from "./pages/AdminCertificates.jsx";
import Certificate, { MyCertificates } from "./pages/Certificate.jsx";
import AdminTBTesting from "./pages/AdminTBTesting.jsx";
import AdminQAMeetings from "./pages/AdminQAMeetings.jsx";
import AdminPCASupervision from "./pages/AdminPCASupervision.jsx";
import AdminRecruiting from "./pages/AdminRecruiting.jsx";
import AdminEnterprise from "./pages/AdminEnterprise.jsx";
import PortalOnboarding from "./pages/PortalOnboarding.jsx";
import AdminOnboarding, { HRSignaturePage } from "./pages/AdminOnboarding.jsx";
import AdminOnboardingPacket from "./pages/AdminOnboardingPacket.jsx";
import InspectorPortal from "./pages/InspectorPortal.jsx";
import ClientSign from "./pages/ClientSign.jsx";
import AdminInspectorAccess from "./pages/AdminInspectorAccess.jsx";
import { AccessProvider, StaffGuard, StaffHome } from "./access.jsx";

function LoadingScreen() {
  return <div className="flex items-center justify-center h-screen text-gray-500">Loading…</div>;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setSessionExpired(true);
    });
  }, []);

  useEffect(() => {
    api.me().then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const isFamily = user?.role === "family";
  const isInspector = user?.role === "inspector";
  const isStaff = user?.role === "staff";
  const isAdminUI = isAdmin || isStaff; // staff see admin pages, filtered by StaffGuard + server permissions
  const login = (authenticatedUser) => {
    setUser(authenticatedUser);
    setSessionExpired(false);
  };
  const logout = () => {
    setUser(null);
    setSessionExpired(false);
  };

  return (
    <AccessProvider user={user}>
    <Routes>
      <Route path="/family-portal/:token" element={<FamilyPortal />} />
      <Route path="/sign/:token" element={<ClientSign />} />
      <Route path="/apply" element={<PublicApply />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />

      <Route
        path="/admin/employees/:employeeId/id-card-print"
        element={
          loading ? <LoadingScreen /> : !user ? <Login sessionExpired={sessionExpired} onLogin={login} /> : isAdmin ? <IDCardPrint /> : <Navigate to="/" replace />
        }
      />

      {/* Read-only inspection portal: inspectors only, or administrators previewing it */}
      <Route
        path="/inspector/*"
        element={
          loading ? <LoadingScreen /> : !user ? <Login sessionExpired={sessionExpired} onLogin={login} /> : (isInspector || isAdminUI) ? <InspectorPortal user={user} onLogout={logout} /> : <Navigate to="/" replace />
        }
      />

      <Route
        path="/*"
        element={
          loading ? (
            <LoadingScreen />
          ) : !user ? (
            <Login sessionExpired={sessionExpired} onLogin={login} />
          ) : isInspector ? (
            <Navigate to="/inspector" replace />
          ) : (
            <>
              <GlobalTopBar user={user} onLogout={logout} onMenuClick={() => setMobileNavOpen(true)} />
              <Layout user={user} onLogout={logout} sidebarOpen={mobileNavOpen} onCloseSidebar={() => setMobileNavOpen(false)}>
                <StaffGuard>
                <Routes>
                  <Route path="/" element={isAdmin ? <AdminOverview /> : isStaff ? <StaffHome user={user} /> : isFamily ? <Navigate to="/family" replace /> : <EmployeePortal />} />
                  <Route path="/portal/schedule" element={!isAdmin ? <PortalSchedule /> : <Navigate to="/" replace />} />
                  <Route path="/portal/onboarding" element={<PortalOnboarding />} />
                  <Route path="/portal/onboarding/:formId" element={<PortalOnboarding />} />
                  <Route path="/admin/inspector-access" element={isAdminUI ? <AdminInspectorAccess /> : <Navigate to="/" replace />} />
                  <Route path="/admin/onboarding" element={isAdminUI ? <AdminOnboarding /> : <Navigate to="/" replace />} />
                  <Route path="/admin/onboarding/signature" element={isAdminUI ? <HRSignaturePage /> : <Navigate to="/" replace />} />
                  <Route path="/admin/onboarding/:packetId" element={isAdminUI ? <AdminOnboardingPacket /> : <Navigate to="/" replace />} />
                  <Route path="/admin/onboarding/:packetId/forms/:formId" element={isAdminUI ? <AdminOnboardingPacket /> : <Navigate to="/" replace />} />
                  <Route path="/portal/compliance" element={!isAdmin ? <PortalCompliance /> : <Navigate to="/" replace />} />
                  <Route path="/portal/training" element={!isAdmin ? <CourseCatalog /> : <Navigate to="/" replace />} />
                  <Route path="/courses/:id" element={<CoursePlayer />} />
                  <Route path="/admin" element={isAdminUI ? <AdminCourses /> : <Navigate to="/" replace />} />
                  <Route path="/admin/courses/:id" element={isAdminUI ? <AdminCourseEdit /> : <Navigate to="/" replace />} />
                  <Route path="/admin/dashboard" element={isAdminUI ? <AdminDashboard /> : <Navigate to="/" replace />} />
                  <Route path="/admin/users" element={isAdminUI ? <Users /> : <Navigate to="/" replace />} />
                  <Route path="/admin/hr" element={isAdminUI ? <HRHome /> : <Navigate to="/" replace />} />
                  <Route path="/admin/hr/:resource" element={isAdminUI ? <HRModule /> : <Navigate to="/" replace />} />
                  <Route path="/admin/scheduling" element={isAdminUI ? <Scheduling /> : <Navigate to="/" replace />} />
                  <Route path="/admin/accounting" element={isAdminUI ? <Accounting /> : <Navigate to="/" replace />} />
                  <Route path="/admin/reports" element={isAdminUI ? <Reports /> : <Navigate to="/" replace />} />
                  <Route path="/admin/data-migration" element={isAdminUI ? <DataMigration /> : <Navigate to="/" replace />} />
                  <Route path="/admin/clients" element={isAdminUI ? <Clients /> : <Navigate to="/" replace />} />
                  <Route path="/admin/clients/new" element={isAdminUI ? <ClientForm /> : <Navigate to="/" replace />} />
                  <Route path="/admin/clients/:clientId" element={isAdminUI ? <ClientProfile /> : <Navigate to="/" replace />} />
                  <Route path="/admin/clients/:clientId/edit" element={isAdminUI ? <ClientForm /> : <Navigate to="/" replace />} />
                  <Route path="/admin/employees" element={isAdminUI ? <Employees /> : <Navigate to="/" replace />} />
                  <Route path="/admin/employees/new" element={isAdminUI ? <EmployeeForm /> : <Navigate to="/" replace />} />
                  <Route path="/admin/employees/:employeeId" element={isAdminUI ? <EmployeeProfile /> : <Navigate to="/" replace />} />
                  <Route path="/admin/employees/:employeeId/edit" element={isAdminUI ? <EmployeeForm /> : <Navigate to="/" replace />} />
                  <Route path="/admin/caregivers" element={isAdminUI ? <Employees /> : <Navigate to="/" replace />} />
                  <Route path="/admin/recruitment/:id" element={isAdminUI ? <ApplicantProfile /> : <Navigate to="/" replace />} />
                  <Route path="/portal/communications" element={<PortalCommunications />} />
                  <Route path="/admin/communications" element={isAdminUI ? <CommunicationSupervision /> : <Navigate to="/" replace />} />
                  <Route path="/portal/evv" element={<PortalEVV />} />
                  <Route path="/admin/evv" element={isAdminUI ? <EVVMonitor /> : <Navigate to="/" replace />} />
                  <Route path="/portal/visits/:visitId/care" element={<PortalVisitCare />} />
                  <Route path="/portal/visits/:visitId/note" element={<PortalProgressNote />} />
                  <Route path="/admin/progress-notes" element={isAdminUI ? <AdminProgressNotes /> : <Navigate to="/" replace />} />
                  <Route path="/portal/visits/:visitId/verify" element={<PortalVisitVerification />} />
                  <Route path="/admin/visit-verification" element={isAdminUI ? <AdminVisitVerification /> : <Navigate to="/" replace />} />
                  <Route path="/portal/mileage" element={<PortalMileage />} />
                  <Route path="/admin/mileage" element={isAdminUI ? <AdminMileage /> : <Navigate to="/" replace />} />
                  <Route path="/portal/timesheets" element={<PortalTimesheets />} />
                  <Route path="/admin/payroll" element={isAdminUI ? <AdminPayroll /> : <Navigate to="/" replace />} />
                  <Route path="/admin/operations" element={isAdminUI ? <AdminOperations /> : <Navigate to="/" replace />} />
                  <Route path="/admin/intelligence" element={isAdminUI ? <AdminIntelligence /> : <Navigate to="/" replace />} />
                  <Route path="/admin/billing" element={isAdminUI ? <AdminBilling /> : <Navigate to="/" replace />} />
                  <Route path="/admin/crm" element={isAdminUI ? <AdminCRM /> : <Navigate to="/" replace />} />
                  <Route path="/admin/executive" element={isAdminUI ? <AdminExecutiveBI /> : <Navigate to="/" replace />} />
                  <Route path="/family" element={<FamilyMobilePortal />} />
                  <Route path="/portal/mobile" element={<CaregiverMobilePortal />} />
                  <Route path="/admin/quality" element={isAdminUI ? <AdminQualityCenter /> : <Navigate to="/" replace />} />
                  <Route path="/admin/dhhs/dementia-training" element={isAdminUI ? <AdminDementiaTraining /> : <Navigate to="/" replace />} />
                  <Route path="/admin/training" element={isAdminUI ? <AdminTraining /> : <Navigate to="/" replace />} />
                  <Route path="/admin/certificates" element={isAdminUI ? <AdminCertificates /> : <Navigate to="/" replace />} />
                  <Route path="/certificate/:id" element={<Certificate />} />
                  <Route path="/certificates" element={<MyCertificates />} />
                  <Route path="/admin/dhhs/tb-testing" element={isAdminUI ? <AdminTBTesting /> : <Navigate to="/" replace />} />
                  <Route path="/admin/dhhs/qa-meetings" element={isAdminUI ? <AdminQAMeetings /> : <Navigate to="/" replace />} />
                  <Route path="/admin/dhhs/pca-supervision" element={isAdminUI ? <AdminPCASupervision /> : <Navigate to="/" replace />} />
                  <Route path="/admin/recruiting" element={isAdminUI ? <AdminRecruiting /> : <Navigate to="/" replace />} />
                  <Route path="/admin/enterprise" element={isAdminUI ? <AdminEnterprise /> : <Navigate to="/" replace />} />
                  <Route path="/admin/care" element={isAdminUI ? <CareHome /> : <Navigate to="/" replace />} />
                  <Route path="/admin/quality-hub" element={isAdminUI ? <QualityHome /> : <Navigate to="/" replace />} />
                  <Route path="/admin/finance" element={isAdminUI ? <FinanceHome /> : <Navigate to="/" replace />} />
                  <Route path="/admin/growth" element={isAdminUI ? <GrowthHome /> : <Navigate to="/" replace />} />
                  <Route path="/admin/administration" element={isAdminUI ? <AdministrationHome /> : <Navigate to="/" replace />} />
                  <Route path="/portal" element={!isAdmin ? <PortalHome /> : <Navigate to="/" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </StaffGuard>
              </Layout>
            </>
          )
        }
      />
    </Routes>
    </AccessProvider>
  );
}
